import {
  ChangeDetectionStrategy,
  Component,
  Input
} from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  merge,
  scan,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { TransactionsDto } from 'src/app/dashboard/bankAccounts/models/transactionsDto.model';
import { TransactionsSearchOptionsDto } from 'src/app/dashboard/bankAccounts/models/transactionsSearchOptionsDto.model';
import { TransactionExportFileType, TransactionsMsService } from 'src/app/dashboard/bankAccounts/transactions-ms.service';
import { SearchOptionsResponse } from '../../models/SearchOptionsResponse.model';

@Component({
  selector: 'terra-plaid-transactions',
  templateUrl: './plaid-transactions.component.html',
  styleUrls: ['./plaid-transactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaidTransactionsComponent extends OnDestroyMixin {
  @Input('bankAccountId') clientBankAccountId: number;

  private readonly DEFAULT_PAGE_SIZE: number = 10;
  totalNumberOfTransactions$: Subject<number> = new Subject();

  private pageSize$ = new BehaviorSubject<number>(this.DEFAULT_PAGE_SIZE);
  private pageNumber$ = new Subject<number>();
  private textFilter$ = new Subject<string>();
  private dateRange$ = new Subject<DateRange<Date>>();
  private direction$ = new Subject<string>();
  private exportFormat$ = new Subject<TransactionExportFileType>();

  private pageSizeSource$ = this.pageSize$.pipe(map(pageSize => ({pageSize}) as TransactionsSearchOptionsDto));
  private pageNumberSource$ = this.pageNumber$.pipe(map(pageNumber => ({pageNumber}) as TransactionsSearchOptionsDto));
  private textFilterSource$ = this.textFilter$.pipe(map(tf => tf.trim()), distinctUntilChanged(), map(filter => ({filter}) as TransactionsSearchOptionsDto));
  private dateRangeSource$ = this.dateRange$.pipe(map(range => ({startDate: range?.start, endDate: range?.end}) as TransactionsSearchOptionsDto));
  private directionSource$ = this.direction$.pipe(map(tt => ({typeName: tt}) as TransactionsSearchOptionsDto));

  // Merged source of search options
  private searchOptionsSources$: Observable<TransactionsSearchOptionsDto> = merge(
    this.textFilterSource$,
    this.pageNumberSource$,
    this.pageSizeSource$,
    this.dateRangeSource$,
    this.directionSource$,
  );

  // Search options state
  searchOptions$: Observable<TransactionsSearchOptionsDto> = this.searchOptionsSources$.pipe(
    untilComponentDestroyed(this),
    scan((acc, value) => {
      // Whenever something changes, unless it's the pageNumber, return to the first page
      const pageNumber = value.pageNumber === undefined ? 0 : value.pageNumber;
      return {...acc, ...value, pageNumber} as TransactionsSearchOptionsDto;
    }, new TransactionsSearchOptionsDto()),
    shareReplay()
  );

  isLoading$ = new BehaviorSubject<boolean>(false);
  transactions$ = this.searchOptions$.pipe(
    untilComponentDestroyed(this),
    filter(searchOptions => searchOptions !== null),
    tap(() => this.isLoading$.next(true)),
    switchMap((searchOptions) =>
      this.transactionsServiceMs.getTransactions(
        this.clientBankAccountId,
        searchOptions
        , 'external'
      )
    ),
    map((res) => res as SearchOptionsResponse<TransactionsDto>),
    filter(response => !!response),
    tap(response => this.totalNumberOfTransactions$.next(response.totalCount)),
    map(response => response.rows.map(item => {
      return {
        ...item,
        showEdit: item.note != null,
        showIcons: false,
      }
    })),
    tap(() => this.isLoading$.next(false)),
    shareReplay()
  );

  constructor(private transactionsServiceMs: TransactionsMsService) {
    super()
  }

  onFilterChanged(val: string) {
    this.textFilter$.next(val);
  }

  onDirectionChanged(val: string) {
    this.direction$.next(val);
  }

  onDateRangeChanged(event: DateRange<Date>) {
    this.dateRange$.next(event);
  }

  onPageSizeChanged(val: number) {
    this.pageSize$.next(val);
  }

  onPageNumberChanged(val: number) {
    this.pageNumber$.next(val);
  }

  onExportSelected(val: TransactionExportFileType) {
    this.exportFormat$.next(val);
  }
}
